import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AppIcon } from "../components/AppIcon";
import type { IconName } from "../assets/icons";
import {
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  MapPin,
  Package,
  Truck,
  Users,
  ArrowRight,
  Building2,
  TrendingUp,
  Shield,
  Zap,
  Target,
  Camera,
  Upload,
  Eye,
  Wrench,
  ShoppingCart,
  Receipt,
  Bell,
  Check,
  ClipboardList,
  AlertTriangle,
  Star,
  Settings,
  Smartphone,
  RotateCcw,
  Edit3,
  UserCheck,
  Download,
  Search,
  Folder,
  FolderOpen,
  File,
  Image,
  Share2,
  Lock,
  Globe,
  Link,
  Archive,
  Trash2,
  Copy,
  Move,
  Plus,
  Grid3X3,
  List,
  Filter,
  SortAsc,
  CloudUpload,
  PaperclipIcon,
  Monitor,
  Tablet,
  FolderPlus,
  UserPlus,
  ShieldCheck,
  Key,
  Activity,
  BarChart3,
  Brain,
  Tag,
  RefreshCw,
  Database,
  HardDrive,
  Cloud,
  Briefcase,
  Home,
  Camera as CameraIcon,
  Video,
  FileImage,
  FileSpreadsheet,
  Calendar as CalendarIcon,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Mail,
} from "lucide-react";

const CounterAnimation = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

const FileDashboardMockup = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
    >
      <div className="bg-[#f5f5f7] px-5 py-3.5 border-b border-gray-200/80">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
              <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
              <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
            </div>
            <h3 className="font-medium text-gray-700 text-sm md:text-base truncate">
              RealEsy WorkDrive
            </h3>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0 text-gray-500">
            <Search className="w-4 h-4" />
            <Bell className="w-4 h-4" />
            <div className="w-7 h-7 rounded-full bg-gray-300"></div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-2 bg-blue-100 rounded-lg">
              <AppIcon name="workdriveSharedFolder" size={20} alt="My Files" />
              <span className="text-sm font-medium">My Files</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <AppIcon name="workdriveSharedStar" size={20} alt="Starred" />
              <span className="text-sm">Starred</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <AppIcon name="workdriveSharedShare" size={20} alt="Shared" />
              <span className="text-sm">Shared</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <AppIcon name="workdriveSharedClock" size={20} alt="Recent" />
              <span className="text-sm">Recent</span>
            </div>
            <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <AppIcon name="workdriveSharedDelete" size={20} alt="Trash" />
              <span className="text-sm">Trash</span>
            </div>
          </div>

          <div className="mt-6">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Team Folders
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <AppIcon name="workdriveSharedFolder" size={16} alt="Marketing" />
                <span className="text-sm">Marketing</span>
              </div>
              <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <AppIcon name="workdriveSharedFolder" size={16} alt="Transactions" />
                <span className="text-sm">Transactions</span>
              </div>
              <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <AppIcon name="workdriveSharedFolder" size={16} alt="Photography" />
                <span className="text-sm">Photography</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Home className="w-5 h-5 text-gray-400" />
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Office 1</span>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">Active Listings</span>
            </div>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline">
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline">
                <List className="w-4 h-4" />
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Upload
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            {(
              [
                {
                  name: "123-Sunset-Blvd",
                  icon: "workdriveSharedFolder",
                  files: "24 files",
                },
                {
                  name: "456-Ocean-Ave",
                  icon: "workdriveSharedFolder",
                  files: "18 files",
                },
                {
                  name: "789-Pine-St",
                  icon: "workdriveSharedFolder",
                  files: "31 files",
                },
                {
                  name: "Contract_Template.pdf",
                  icon: "workdriveSharedPdf",
                  files: "2.4 MB",
                },
                {
                  name: "Property_Photos_1.jpg",
                  icon: "workdriveSharedJpg",
                  files: "8.1 MB",
                },
                {
                  name: "Listing_Sheet.xlsx",
                  icon: "workdriveSharedSheets",
                  files: "1.2 MB",
                },
                {
                  name: "Inspection_Report.pdf",
                  icon: "workdriveSharedPdf",
                  files: "3.7 MB",
                },
                {
                  name: "Marketing_Materials",
                  icon: "workdriveSharedFolder",
                  files: "12 files",
                },
              ] as { name: string; icon: IconName; files: string }[]
            ).map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <div className="flex flex-col items-center text-center">
                  <AppIcon name={item.icon} size={32} alt={item.name} className="mb-2" />
                  <p className="text-sm font-medium text-gray-900 truncate w-full">{item.name}</p>
                  <p className="text-xs text-gray-500">{item.files}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3 mb-2">
              <AppIcon name="workdriveSharedShare" size={20} alt="Shared" />
              <span className="font-medium text-blue-900">Shared with client: Sarah Johnson</span>
            </div>
            <p className="text-sm text-blue-700">Link expires in 7 days • Password protected</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const FeatureSection = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

export default function WorkDriveLandingPage() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const headlineBenefits: {
    icon: IconName;
    title: string;
    description: string;
    cardClassName: string;
    motionX: number;
    delay: number;
  }[] = [
      {
        icon: "workdriveEverythingInOnePlace",
        title: "Everything in One Place",
        description:
          "Organize files by Office, Team, and Listing—no more hunting through email threads, hard drives, or personal cloud accounts.",
        cardClassName: "p-8 rounded-2xl border-2 shadow-md",
        motionX: -30,
        delay: 0.1,
      },
      {
        icon: "workdriveFasterDeals",
        title: "Faster Deals",
        description:
          "Share the right documents instantly with real estate agents, clients, and vendors—using passwords, expiration dates, and role-based permissions.",
        cardClassName: "p-8 rounded-2xl border-2 shadow-md",
        motionX: 30,
        delay: 0.2,
      },
      {
        icon: "workdriveComplianceMadeSimple",
        title: "Compliance Made Simple",
        description:
          "Keep a clean record with brokerage document storage that includes folder-level permissions, activity logs, and version-aware file properties.",
        cardClassName: "p-8 rounded-2xl border-2 shadow-md",
        motionX: -30,
        delay: 0.3,
      },
      {
        icon: "workdriveMobileReady",
        title: "Mobile-Ready",
        description:
          "Upload property photos from jobs, attach files to listings, and move documents on the go.",
        cardClassName: "p-8 rounded-2xl border-2 shadow-md",
        motionX: 30,
        delay: 0.4,
      },
    ];

  const coreFeatures = [
    {
      title: "Centralized, Listing-First Storage",
      description:
        "Organize by Office → Team → Listing. Create folders for each listing, keep assets and contracts together, and maintain continuity from install → live → removal.",
      benefits: [
        "Structured Office/Team/Listing hierarchy for easy navigation",
        "All listing documents, photos, and contracts in one place",
        "Seamless workflow from installation to removal",
        "Consistent organization across all team members",
        "Automatic folder creation for new listings",
      ],
    },
    {
      title: "Team Folders with Advanced Roles",
      description:
        "Create Team Folders for marketing, transactions, and photography. Add members with Owner/Admin/Editor/Viewer roles. Manage members, update folder names/colors, and view team activity at a glance.",
      benefits: [
        "Owner/Admin/Editor/Viewer role-based access control",
        "Real-time team activity tracking and audit logs",
        "Customizable folder names and color coding",
        "Granular permission management for sensitive documents",
        "Instant member addition and removal capabilities",
      ],
    },
    {
      title: "Secure Sharing Controls",
      description:
        "Share to people or generate a link. Choose View or Edit access. Add a password and set an expiry date. See who has access and revoke in one click.",
      benefits: [
        "Password-protected sharing links with expiration dates",
        "View-only or edit permissions for external sharing",
        "One-click access revocation for security",
        "Complete visibility into who has access to what",
        "Secure client document sharing without email attachments",
      ],
    },
    {
      title: "Smart Search & AI Summaries",
      description:
        "Keyword search across file names, metadata, and attached listings. AI-powered document summaries surface key details instantly, helping brokerages act without downloading large files.",
      benefits: [
        "Intelligent search across file names, content, and metadata",
        "AI-powered document summaries for quick insights",
        "Instant preview of key contract details and dates",
        "Cross-listing search to find related documents",
        "Smart filtering by file type, date, and team member",
      ],
    },
    {
      title: "Attach Files to Listings & Jobs",
      description:
        "Attach photos, PDFs, estimates, disclosures, proofs, and invoices directly to a Listing or Order. Everyone sees the same source of truth.",
      benefits: [
        "Direct file attachment to listings and installation orders",
        "Centralized document repository for each property",
        "Automatic linking of job photos and completion proofs",
        "Shared access to all team members involved in the listing",
        "Complete audit trail of all attached documents",
      ],
    },
    {
      title: "Clean, Efficient Interface",
      description:
        "Grid/List view, sorting by name/date/size/type, and breadcrumbs for quick navigation. Properties pane shows type, size, modified date, owner, and sharing activity. Move/Copy/Rename files and create folders in seconds.",
      benefits: [
        "Grid and list view options for different work preferences",
        "Advanced sorting and filtering capabilities",
        "Comprehensive file properties and metadata display",
        "Drag-and-drop file organization",
        "Quick actions for move, copy, rename, and delete",
      ],
    },
    {
      title: "Smart Uploads That Match Real Work",
      description:
        "Drag & drop or use the Upload button. Bulk uploads for photos, reports, and proofs. Mobile flow: capture on site, upload, tag, and attach to the listing.",
      benefits: [
        "Drag-and-drop bulk upload for efficiency",
        "Mobile photo capture with automatic geo-tagging",
        "Direct upload from property visits to specific listings",
        "Automatic file type recognition and organization",
        "Progress tracking for large file uploads",
      ],
    },
    {
      title: "Security & Control",
      description:
        "Role-based access in team folders. Password-protected file sharing with expiry. Clear visibility into who has access and when.",
      benefits: [
        "Enterprise-grade security with role-based permissions",
        "Encrypted file storage and secure transmission",
        "Complete access logs and audit trails",
        "Automated security compliance reporting",
        "Two-factor authentication for sensitive documents",
      ],
    },
  ];

  const workflows: {
    icon: IconName;
    title: string;
    description: string;
    files: string[];
  }[] = [
      {
        title: "Marketing",
        description: "Store brand kits, signage proofs, and social media assets in a shared folder.",
        icon: "workdriveMarketing",
        files: [
          "Brand_Guidelines.pdf",
          "Logo_Assets.zip",
          "Social_Templates.psd",
          "Signage_Proofs.pdf",
        ],
      },
      {
        title: "Transactions",
        description: "Keep contracts, addenda, and inspection reports under each listing.",
        icon: "workdriveTransaction",
        files: [
          "Purchase_Agreement.pdf",
          "Addendum_01.pdf",
          "Inspection_Report.pdf",
          "Title_Docs.pdf",
        ],
      },
      {
        title: "Install/Removal",
        description: "Upload job photos, permits, and completion proofs; attach them to the order.",
        icon: "workdriveInstallRemoval",
        files: ["Install_Photos.jpg", "Completion_Proof.jpg", "Site_Permit.pdf", "Job_Summary.pdf"],
      },
      {
        title: "Brokerage Operations",
        description: "Centralize training materials, policies, and templates.",
        icon: "workdriveBrokerageOperation",
        files: [
          "Training_Manual.pdf",
          "Company_Policies.pdf",
          "Contract_Templates.zip",
          "Forms_Library.zip",
        ],
      },
    ];

  const whatsIncludedSections: {
    icon: IconName;
    title: string;
    features: string[];
  }[] = [
      {
        title: "Essential File Management",
        features: ["My Files", "Recent", "Shared", "Starred", "Trash"],
        icon: "workdriveEssentialFileManagement",
      },
      {
        title: "Team Collaboration",
        features: [
          "Team Folders",
          "Member roles",
          "Activity tracking",
          "Permission management",
        ],
        icon: "workdriveTeamCollaboration",
      },
      {
        title: "File Operations",
        features: [
          "Move/Copy/Rename",
          "Create folders",
          "Bulk uploads",
          "File properties",
        ],
        icon: "workdriveFileOperation",
      },
      {
        title: "Secure Sharing",
        features: [
          "Password protection",
          "Link expiry",
          "Access control",
          "Share history",
        ],
        icon: "workdriveSecureSharing",
      },
      {
        title: "Search & Discovery",
        features: ["Smart search", "AI summaries", "File filtering", "Quick preview"],
        icon: "workdriveSearchDiscovery",
      },
      {
        title: "Mobile Access",
        features: ["Mobile app", "Photo capture", "Offline sync", "Push notifications"],
        icon: "workdriveMobileAccess",
      },
    ];

  const faqs = [
    {
      q: "Can I move files between listing folders?",
      a: "Yes. Use Move to re-file any item. Or Copy when the same document belongs in two places.",
    },
    {
      q: "Can I review team folder permissions?",
      a: "Yes. Manage members, adjust roles, and remove access from the Team Folder view.",
    },
    {
      q: "Can I see file details?",
      a: "Yes. Open Properties to view metadata, previews, and sharing status.",
    },
    {
      q: "Can I recover deleted files?",
      a: "Yes. Use Trash to restore items within the retention window.",
    },
    {
      q: "How does the AI document summary work?",
      a: "Our AI analyzes documents to extract key information like dates, parties, and important terms, providing instant summaries without downloading.",
    },
    {
      q: "What file types are supported?",
      a: "We support all common real estate file types including PDFs, images, videos, spreadsheets, documents, and specialized formats.",
    },
    {
      q: "Is my data secure?",
      a: "Yes. All files are encrypted in transit and at rest, with enterprise-grade security, audit logs, and compliance with industry standards.",
    },
    {
      q: "Can I access files offline?",
      a: "Selected files can be downloaded for offline access, and the mobile app provides offline viewing for recently accessed documents.",
    },
  ];

  return (
    <>
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-[#eef5ff] via-white to-[#fff6eb] py-20 md:py-32 overflow-hidden">
        {/* Animated Gradient Background */}
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              "radial-gradient(600px circle at 18% 28%, rgba(35, 109, 217, 0.16), transparent 52%), radial-gradient(780px circle at 82% 68%, rgba(245, 139, 1, 0.14), transparent 52%), radial-gradient(420px circle at 48% 82%, rgba(7, 51, 153, 0.10), transparent 50%)",
              "radial-gradient(600px circle at 78% 22%, rgba(245, 139, 1, 0.15), transparent 52%), radial-gradient(780px circle at 18% 78%, rgba(35, 109, 217, 0.16), transparent 52%), radial-gradient(420px circle at 62% 42%, rgba(255, 176, 64, 0.12), transparent 50%)",
              "radial-gradient(600px circle at 18% 28%, rgba(35, 109, 217, 0.16), transparent 52%), radial-gradient(780px circle at 82% 68%, rgba(245, 139, 1, 0.14), transparent 52%), radial-gradient(420px circle at 48% 82%, rgba(7, 51, 153, 0.10), transparent 50%)",
            ],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />

        {/* Floating Background Elements */}
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-30">
          <div className="absolute top-16 left-8 w-72 h-72 rounded-full blur-3xl" style={{ background: "rgba(35, 109, 217, 0.30)" }}></div>
          <div className="absolute bottom-16 right-8 w-[28rem] h-[28rem] rounded-full blur-3xl" style={{ background: "rgba(245, 139, 1, 0.22)" }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full blur-3xl" style={{ background: "rgba(7, 51, 153, 0.14)" }}></div>
        </motion.div>

        {/* Floating File Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(12)].map((_, i) => {
            const startX = Math.random() * 100;
            const startY = 100 + Math.random() * 20;
            const endX = Math.random() * 100;
            const endY = -20 - Math.random() * 20;

            return (
              <motion.div
                key={i}
                initial={{
                  opacity: 0,
                  left: `${startX}%`,
                  top: `${startY}%`,
                }}
                animate={{
                  opacity: [0, 0.55, 0.35, 0.7, 0],
                  left: `${endX}%`,
                  top: `${endY}%`,
                  rotate: [0, 360],
                }}
                transition={{
                  duration: Math.random() * 20 + 15,
                  repeat: Infinity,
                  delay: i * 2,
                  ease: "linear",
                }}
                className="absolute"
              >
                {i % 4 === 0 ? (
                  <Folder className="w-8 h-8 text-[#236DD9]/45" />
                ) : i % 4 === 1 ? (
                  <File className="w-6 h-6 text-[#f58b01]/40" />
                ) : i % 4 === 2 ? (
                  <FileImage className="w-7 h-7 text-[#073399]/35" />
                ) : (
                  <FileText className="w-6 h-6 text-[#ffb040]/45" />
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Geometric Shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => {
            const startX = Math.random() * 100;
            const startY = Math.random() * 100;
            const endX = Math.random() * 100;
            const endY = Math.random() * 100;

            return (
              <motion.div
                key={`geo-${i}`}
                initial={{
                  opacity: 0,
                  left: `${startX}%`,
                  top: `${startY}%`,
                  scale: 0,
                }}
                animate={{
                  opacity: [0, 0.12, 0.06, 0.16, 0],
                  left: `${endX}%`,
                  top: `${endY}%`,
                  scale: [0, 1, 1.2, 0.8, 0],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: Math.random() * 25 + 20,
                  repeat: Infinity,
                  delay: i * 3,
                  ease: "easeInOut",
                }}
                className={`absolute ${i % 3 === 0
                  ? "w-20 h-20 border-2 border-[#236DD9]/25"
                  : i % 3 === 1
                    ? "w-16 h-16 border-2 border-[#f58b01]/30"
                    : "w-12 h-12 border-2 border-[#073399]/20"
                  } ${i % 2 === 0 ? "rounded-full" : "rounded-lg rotate-45"}`}
              />
            );
          })}
        </div>

        {/* Particle System */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => {
            const startX = Math.random() * 100;
            const endX = Math.random() * 100 + Math.sin(i) * 10;
            const particleColors = ["#236DD9", "#f58b01", "#073399"];

            return (
              <motion.div
                key={`particle-${i}`}
                initial={{
                  opacity: 0,
                  left: `${startX}%`,
                  top: "105%",
                }}
                animate={{
                  opacity: [0, 0.8, 0.3, 0.6, 0],
                  left: `${endX}%`,
                  top: "-5%",
                  scale: [0, 1, 0.5, 1, 0],
                }}
                transition={{
                  duration: Math.random() * 30 + 25,
                  repeat: Infinity,
                  delay: i * 1.5,
                  ease: "linear",
                }}
                className="absolute w-1 h-1 rounded-full"
                style={{ background: particleColors[i % 3] }}
              />
            );
          })}
        </div>

        {/* Glow Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              opacity: [0.28, 0.5, 0.28],
              scale: [1, 1.18, 1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full blur-3xl opacity-20"
            style={{ background: "linear-gradient(120deg, #073399 0%, #236DD9 100%)" }}
          />
          <motion.div
            animate={{
              opacity: [0.2, 0.42, 0.2],
              scale: [1.15, 1, 1.15],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-15"
            style={{ background: "linear-gradient(240deg, #f58b01 0%, #ffb040 100%)" }}
          />
        </div>

        {/* Floating Data Streams */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => {
            const leftPos = Math.random() * 80 + 10;
            const topPos = Math.random() * 60 + 20;

            return (
              <motion.div
                key={`stream-${i}`}
                initial={{
                  opacity: 0,
                  pathLength: 0,
                }}
                animate={{
                  opacity: [0, 0.3, 0.1, 0.4, 0],
                  pathLength: [0, 1, 1, 0],
                }}
                transition={{
                  duration: Math.random() * 15 + 10,
                  repeat: Infinity,
                  delay: i * 4,
                  ease: "easeInOut",
                }}
                className="absolute"
                style={{
                  left: `${leftPos}%`,
                  top: `${topPos}%`,
                }}
              >
                <svg width="200" height="100" viewBox="0 0 200 100" className="opacity-30">
                  <motion.path
                    d={`M0,50 Q50,${Math.random() * 40 + 30} 100,50 T200,50`}
                    stroke={i % 2 === 0 ? "#236DD9" : "#f58b01"}
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray="5,5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: [0, 1, 0] }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      delay: i * 2,
                      ease: "easeInOut",
                    }}
                  />
                </svg>
              </motion.div>
            );
          })}
        </div>

        {/* Floating Cloud Storage Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => {
            const startY = 20 + Math.random() * 60;
            const endY = 20 + Math.random() * 60;

            return (
              <motion.div
                key={`cloud-${i}`}
                initial={{
                  opacity: 0,
                  left: "-10%",
                  top: `${startY}%`,
                }}
                animate={{
                  opacity: [0, 0.4, 0.2, 0.6, 0],
                  left: "110%",
                  top: [`${startY}%`, `${endY}%`, `${startY}%`],
                  rotate: [0, 10, -10, 5, 0],
                }}
                transition={{
                  duration: Math.random() * 25 + 30,
                  repeat: Infinity,
                  delay: i * 8,
                  ease: "linear",
                }}
                className="absolute"
              >
                <Cloud className="w-12 h-12 text-blue-300" />
              </motion.div>
            );
          })}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    RealEsy WorkDrive — Real Estate File Management System for Brokerages & Agents
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    Your brokerage's secure cloud drive for real estate listings, contracts, photos,
                    and team collaboration—built right into RealEsy.
                  </p>
                </div>

                <div className="flex justify-center pt-8">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-12 sm:px-16 py-6 sm:py-7 text-lg sm:text-xl font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.05] transition-all duration-300 border-0"
                  >
                    Get Started
                    <ArrowRight className="ml-3 w-6 h-6" />
                  </Button>
                </div>
              </div>
            </motion.div>

            <div className="max-w-6xl mx-auto">
              <FileDashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Headline Benefits Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-8">
                Everything You Need for Real Estate File Management
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {headlineBenefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: benefit.motionX }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: benefit.delay }}
                  className={benefit.cardClassName}
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6">
                    <AppIcon name={benefit.icon} size={36} alt={benefit.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Why Brokerages Love WorkDrive Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side: Illustration */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:pr-8"
              >
                <div className="relative bg-white rounded-2xl shadow-xl p-8 border border-gray-200 overflow-hidden">
                  {/* Background glow effects */}
                  <div className="absolute top-0 left-0 w-32 h-32 bg-blue-100 rounded-full blur-3xl opacity-30 -translate-x-16 -translate-y-16"></div>
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-purple-100 rounded-full blur-2xl opacity-40 translate-x-12 translate-y-12"></div>

                  <div className="relative space-y-8">
                    {/* Listing-Centric Structure */}
                    <motion.div
                      className="space-y-4"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                    >
                      <motion.h4
                        className="font-bold text-lg text-gray-900 flex items-center gap-3"
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <motion.div
                          className="w-8 h-8 flex items-center justify-center"
                          whileHover={{ rotate: 5, scale: 1.1 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <AppIcon
                            name="workdriveSharedListingCentricStructure"
                            size={24}
                            alt="Listing-Centric Structure"
                          />
                        </motion.div>
                        Listing-Centric Structure
                      </motion.h4>
                      <div className="pl-8 space-y-3">
                        <motion.div
                          className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 shadow-sm"
                          whileHover={{ x: 5, boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)" }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <motion.div
                            whileHover={{ rotate: 15 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <AppIcon name="workdriveSharedFolder" size={20} alt="Listing folder" />
                          </motion.div>
                          <span className="font-medium">123 Sunset Boulevard</span>
                        </motion.div>
                        <div className="pl-6 space-y-3">
                          {(
                            [
                              {
                                icon: "workdriveSharedPropertyPhotos",
                                text: "Property Photos (24)",
                                bgColor: "hover:bg-green-50",
                              },
                              {
                                icon: "workdriveSharedContractDisclosure",
                                text: "Contracts & Disclosures",
                                bgColor: "hover:bg-red-50",
                              },
                              {
                                icon: "workdriveSharedSignageProof",
                                text: "Signage Proofs",
                                bgColor: "hover:bg-purple-50",
                              },
                              {
                                icon: "workdriveSharedInvoiceReceipt",
                                text: "Invoices & Receipts",
                                bgColor: "hover:bg-orange-50",
                              },
                            ] as { icon: IconName; text: string; bgColor: string }[]
                          ).map((item, index) => (
                            <motion.div
                              key={index}
                              className={`flex items-center gap-3 text-sm p-2 rounded-lg transition-all duration-200 ${item.bgColor} cursor-pointer`}
                              initial={{ opacity: 0, x: -10 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ delay: 0.4 + index * 0.1 }}
                              whileHover={{ x: 3, scale: 1.02 }}
                            >
                              <motion.div
                                whileHover={{ scale: 1.2, rotate: 5 }}
                                transition={{ type: "spring", stiffness: 300 }}
                              >
                                <AppIcon name={item.icon} size={16} alt={item.text} />
                              </motion.div>
                              <span>{item.text}</span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>

                    {/* Team Controls */}
                    <motion.div
                      className="space-y-4"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                    >
                      <motion.h4
                        className="font-bold text-lg text-gray-900 flex items-center gap-3"
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <motion.div
                          className="w-8 h-8 flex items-center justify-center"
                          whileHover={{ rotate: -5, scale: 1.1 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <AppIcon name="workdriveSharedTeamControl" size={24} alt="Team Controls" />
                        </motion.div>
                        Team Controls
                      </motion.h4>
                      <div className="grid grid-cols-2 gap-3">
                        {(
                          [
                            {
                              icon: "workdriveSharedOwner",
                              bg: "bg-white",
                              border: "border-gray-200",
                              label: "Owner",
                              hoverBg: "hover:bg-gray-100",
                            },
                            {
                              icon: "workdriveSharedAdmin",
                              bg: "bg-white",
                              border: "border-gray-200",
                              label: "Admin",
                              hoverBg: "hover:bg-gray-100",
                            },
                            {
                              icon: "workdriveSharedEditor",
                              bg: "bg-white",
                              border: "border-gray-200",
                              label: "Editor",
                              hoverBg: "hover:bg-gray-100",
                            },
                            {
                              icon: "workdriveSharedViewer",
                              bg: "bg-white",
                              border: "border-gray-200",
                              label: "Viewer",
                              hoverBg: "hover:bg-gray-100",
                            },
                          ] as {
                            icon: IconName;
                            bg: string;
                            border: string;
                            label: string;
                            hoverBg: string;
                          }[]
                        ).map((item, index) => (
                          <motion.div
                            key={index}
                            className={`p-4 ${item.bg} ${item.hoverBg} rounded-lg text-center border ${item.border} cursor-pointer transition-all duration-200`}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.8 + index * 0.1 }}
                            whileHover={{
                              y: -3,
                              scale: 1.05,
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <motion.div
                              whileHover={{ rotate: 10 }}
                              transition={{ type: "spring", stiffness: 300 }}
                              className="flex justify-center mb-2"
                            >
                              <AppIcon name={item.icon} size={20} alt={item.label} />
                            </motion.div>
                            <span className="text-xs font-medium">{item.label}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>

                    {/* Secure Sharing */}
                    <motion.div
                      className="relative p-4 text-white rounded-lg shadow-lg overflow-hidden"
                      style={{ background: "linear-gradient(90deg, #073399 0%, #236DD9 100%)" }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 1.2, duration: 0.5 }}
                      whileHover={{ scale: 1.02, boxShadow: "0 8px 25px rgba(35, 109, 217, 0.35)" }}
                    >
                      {/* Animated background gradient overlay */}
                      <motion.div
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(90deg, rgba(7, 51, 153, 0.35) 0%, rgba(35, 109, 217, 0.35) 100%)",
                        }}
                        animate={{
                          x: ["-100%", "100%"],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />

                      <div className="relative">
                        <motion.div
                          className="flex items-center gap-3 mb-2"
                          whileHover={{ x: 2 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <motion.div
                            whileHover={{ rotate: 15 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <AppIcon
                              name="workdriveSharedSecurityFeatures"
                              size={20}
                              alt="Secure External Sharing"
                            />
                          </motion.div>
                          <span className="font-medium">Secure External Sharing</span>
                        </motion.div>
                        <motion.p
                          className="text-sm text-blue-100"
                          initial={{ opacity: 0.8 }}
                          whileHover={{ opacity: 1 }}
                        >
                          Password protected • Expires in 7 days • View only access
                        </motion.p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>

              {/* Right side: Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                    Why Brokerages Love WorkDrive
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                    Purpose-built for real estate workflows with listing-centric organization, team
                    collaboration, and secure client sharing.
                  </p>
                </div>

                <div className="space-y-6">
                  {[
                    {
                      title: "Listing-centric document management",
                      description:
                        "Create a folder once, and everything—photos, disclosures, signage proofs, contracts, and invoices—lives there.",
                    },
                    {
                      title: "Office & Team controls",
                      description:
                        "Share team folders with granular roles (Owner/Admin/Editor/Viewer). Manage members, see activity, and revoke access in seconds.",
                    },
                    {
                      title: "Secure external file sharing",
                      description:
                        "Generate view/edit links with passwords and expiry dates. Turn sharing off anytime.",
                    },
                    {
                      title: "Transparency for audits",
                      description:
                        "File and folder properties show size, type, modified time, and who shared it. Trash supports restore to fix mistakes.",
                    },
                    {
                      title: "Attach to workflows",
                      description:
                        "Pin important files to Sales Orders, Installation, and Removal Jobs for full context.",
                    },
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Why Agents Adopt It Quickly Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side: Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                    Why Agents Adopt It Quickly
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                    Designed for real estate professionals who need to access and share documents
                    quickly, whether in the office or on the go.
                  </p>
                </div>

                <div className="space-y-6">
                  {(
                    [
                      {
                        title: "No learning curve",
                        description:
                          "A modern, intuitive real estate file management system with grid/list toggle, breadcrumbs, and quick actions.",
                        icon: "workdriveNoLearningCurve",
                      },
                      {
                        title: "One link per client",
                        description:
                          "Share a single folder for offers, reports, and media; set an expiry and move on.",
                        icon: "workdriveOneLinkPerClient",
                      },
                      {
                        title: "Star favorites",
                        description:
                          "Keep active listings and transaction documents at your fingertips.",
                        icon: "workdriveStarFavourite",
                      },
                      {
                        title: "Work anywhere",
                        description:
                          "Upload from site visits, attach files to a listing, and stay productive on the move.",
                        icon: "workdriveWorkAnywhere",
                      },
                    ] as const
                  ).map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center flex-shrink-0">
                        <AppIcon name={feature.icon} size={28} alt={feature.title} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right side: Agent Interface Mockup */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:pl-8"
              >
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                  {/* Interface Header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AppIcon name="workdriveSharedStar" size={20} alt="Starred" />
                        <span className="font-medium">Starred Items</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Grid3X3 className="w-4 h-4 text-blue-600" />
                        <List className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Starred Files */}
                  <div className="p-6 space-y-4">
                    {(
                      [
                        {
                          name: "Active Listings",
                          files: "12 properties",
                          icon: "workdriveSharedFolder",
                        },
                        {
                          name: "Client_Contracts.pdf",
                          files: "Modified 2 hrs ago",
                          icon: "workdriveSharedPdf",
                        },
                        {
                          name: "Transaction_Docs",
                          files: "8 files",
                          icon: "workdriveSharedFolder",
                        },
                        {
                          name: "Property_Photos.zip",
                          files: "Shared with client",
                          icon: "workdriveSharedPropertyPhotoZip",
                        },
                      ] as { name: string; files: string; icon: IconName }[]
                    ).map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                      >
                        <AppIcon name={item.icon} size={24} alt={item.name} />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-gray-500">{item.files}</p>
                        </div>
                        <AppIcon name="workdriveSharedStar" size={16} alt="Starred" />
                      </motion.div>
                    ))}
                  </div>

                  {/* Quick Actions */}
                  <div className="px-6 pb-6">
                    <div className="grid grid-cols-2 gap-3">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                        <CloudUpload className="w-4 h-4 mr-2" />
                        Upload
                      </Button>
                      <Button variant="outline">
                        <AppIcon name="workdriveSharedShare" size={16} alt="Share" className="mr-2" />
                        Share
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Core Features Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-blue-600 mb-6">Core Features</h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Everything you need to manage real estate documents efficiently with
                enterprise-grade security and intelligent organization.
              </p>
            </div>

            <div className="space-y-24">
              {coreFeatures.map((feature, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? "lg:grid-flow-col-dense" : ""}`}
                >
                  {/* Text Content */}
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className={`space-y-6 ${index % 2 === 1 ? "lg:col-start-2" : ""}`}
                  >
                    <div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed mb-6">
                        {feature.description}
                      </p>
                    </div>

                    <div className="space-y-4">
                      {feature.benefits.map((benefit, benefitIndex) => (
                        <motion.div
                          key={benefitIndex}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.1 + benefitIndex * 0.1 }}
                          className="flex items-start gap-3"
                        >
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-gray-700">{benefit}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Illustration */}
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className={`${index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}`}
                  >
                    {/* Dynamic feature-specific illustrations */}
                    {index === 0 && (
                      // Centralized Storage Illustration
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="text-center">
                            <h4 className="font-bold text-lg mb-4">Organization Structure</h4>
                          </div>
                          <div className="space-y-4">
                            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                              <AppIcon name="workdriveSharedDowntownOffice" size={20} alt="Downtown Office" />
                              <span className="font-medium">Downtown Office</span>
                            </div>
                            <div className="pl-6 space-y-3">
                              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                                <AppIcon name="workdriveSharedSalesTeam" size={20} alt="Sales Team" />
                                <span className="font-medium">Sales Team</span>
                              </div>
                              <div className="pl-6 space-y-2">
                                <div className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                  <AppIcon name="workdriveSharedPlaceholder" size={16} alt="123 Sunset Blvd" />
                                  <span className="text-sm">123 Sunset Blvd</span>
                                </div>
                                <div className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                  <AppIcon name="workdriveSharedPlaceholder" size={16} alt="456 Ocean Ave" />
                                  <span className="text-sm">456 Ocean Ave</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 1 && (
                      // Team Folders with Roles
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <h4 className="font-bold text-lg text-center">Team Folder Management</h4>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <AppIcon name="workdriveSharedFolder" size={20} alt="Marketing Team" />
                                <span className="font-medium">Marketing Team</span>
                              </div>
                              <UserPlus className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="p-3 bg-white rounded-lg border border-gray-200 text-center">
                                <div className="flex justify-center mb-1">
                                  <AppIcon name="workdriveSharedOwner" size={20} alt="Owner" />
                                </div>
                                <div className="text-xs font-medium">Owner</div>
                                <div className="text-xs text-gray-500">Sarah J.</div>
                              </div>
                              <div className="p-3 bg-white rounded-lg border border-gray-200 text-center">
                                <div className="flex justify-center mb-1">
                                  <AppIcon name="workdriveSharedAdmin" size={20} alt="Admin" />
                                </div>
                                <div className="text-xs font-medium">Admin</div>
                                <div className="text-xs text-gray-500">Mike C.</div>
                              </div>
                              <div className="p-3 bg-white rounded-lg border border-gray-200 text-center">
                                <div className="flex justify-center mb-1">
                                  <AppIcon name="workdriveSharedEditor" size={20} alt="Editor" />
                                </div>
                                <div className="text-xs font-medium">Editor</div>
                                <div className="text-xs text-gray-500">Lisa R.</div>
                              </div>
                              <div className="p-3 bg-white rounded-lg border border-gray-200 text-center">
                                <div className="flex justify-center mb-1">
                                  <AppIcon name="workdriveSharedViewer" size={20} alt="Viewer" />
                                </div>
                                <div className="text-xs font-medium">Viewer</div>
                                <div className="text-xs text-gray-500">John D.</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 2 && (
                      // Secure Sharing Controls
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <h4 className="font-bold text-lg text-center">Secure Sharing</h4>
                          <div className="space-y-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3 mb-3">
                                <AppIcon name="workdriveSharedShare" size={20} alt="Share" />
                                <span className="font-medium">Share with Client</span>
                              </div>
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm">Access Type:</span>
                                  <Badge className="bg-green-100 text-green-800">View Only</Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm">Password:</span>
                                  <div className="flex items-center gap-2">
                                    <Lock className="w-4 h-4 text-green-600" />
                                    <span className="text-sm text-green-600">Protected</span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm">Expires:</span>
                                  <span className="text-sm text-gray-600">7 days</span>
                                </div>
                              </div>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2 text-sm">
                                <Link className="w-4 h-4 text-gray-500" />
                                <span className="font-mono text-xs">
                                  RealEsy.io/share/abc123...
                                </span>
                                <Copy className="w-4 h-4 text-gray-400 cursor-pointer" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 3 && (
                      // Smart Search & AI
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <h4 className="font-bold text-lg text-center">Smart Search & AI</h4>
                          <div className="space-y-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3">
                                <Search className="w-5 h-5 text-blue-600" />
                                <input
                                  className="flex-1 bg-transparent border-none outline-none placeholder-gray-500"
                                  placeholder="Search contracts, dates, parties..."
                                  value="contract sunset boulevard"
                                  readOnly
                                />
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3 mb-2">
                                  <AppIcon name="workdriveSharedAiSummary" size={16} alt="AI Summary" />
                                  <span className="font-medium text-sm">AI Summary</span>
                                </div>
                                <p className="text-xs text-gray-600">
                                  Purchase agreement for 123 Sunset Blvd • $1.2M • Closing: Dec 15,
                                  2024 • Buyer: Johnson Family Trust
                                </p>
                              </div>
                              <div className="p-3 bg-white rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <AppIcon name="workdriveSharedPdf" size={16} alt="PDF" />
                                    <span className="text-sm font-medium">
                                      Purchase_Agreement.pdf
                                    </span>
                                  </div>
                                  <Badge className="bg-blue-100 text-blue-800 text-xs">Match</Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 4 && (
                      // Attach to Listings & Jobs
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <h4 className="font-bold text-lg text-center">Attach to Workflows</h4>
                          <div className="space-y-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3 mb-3">
                                <AppIcon name="workdriveSharedAddress" size={20} alt="Listing" />
                                <span className="font-medium">123 Sunset Boulevard</span>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <AppIcon name="workdriveSharedAttachFile" size={16} alt="Attach" />
                                  <span>Property_Photos.zip (24 files)</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <AppIcon name="workdriveSharedAttachFile" size={16} alt="Attach" />
                                  <span>Purchase_Agreement.pdf</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <AppIcon name="workdriveSharedAttachFile" size={16} alt="Attach" />
                                  <span>Install_Order_Photos.jpg</span>
                                </div>
                              </div>
                            </div>
                            <div className="p-4 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3 mb-3">
                                <AppIcon name="workdriveSharedFastDelivery" size={20} alt="Installation" />
                                <span className="font-medium">Installation Order #456</span>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <AppIcon name="workdriveSharedAttachFile" size={16} alt="Attach" />
                                  <span>Completion_Photos.jpg</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <AppIcon name="workdriveSharedAttachFile" size={16} alt="Attach" />
                                  <span>Installation_Receipt.pdf</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 5 && (
                      // Clean Interface
                      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Grid3X3 className="w-4 h-4 text-blue-600" />
                              <List className="w-4 h-4 text-gray-400" />
                            </div>
                            <div className="flex items-center gap-2">
                              <SortAsc className="w-4 h-4 text-gray-400" />
                              <Filter className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-3 gap-3">
                            {(
                              [
                                { name: "Contracts", icon: "workdriveSharedFolder" },
                                { name: "Photos", icon: "workdriveSharedFolder" },
                                { name: "Reports", icon: "workdriveSharedFolder" },
                                { name: "Agreement.pdf", icon: "workdriveSharedPdf" },
                                { name: "Photos.zip", icon: "workdriveSharedPropertyPhotoZip" },
                                { name: "Sheet.xlsx", icon: "workdriveSharedSheets" },
                              ] as { name: string; icon: IconName }[]
                            ).map((item, idx) => (
                              <div key={idx} className="p-3 bg-gray-50 rounded-lg text-center">
                                <div className="flex justify-center mb-2">
                                  <AppIcon name={item.icon} size={24} alt={item.name} />
                                </div>
                                <p className="text-xs font-medium truncate">{item.name}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 6 && (
                      // Smart Uploads
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <h4 className="font-bold text-lg text-center">Smart Uploads</h4>
                          <div className="space-y-4">
                            <div className="p-4 rounded-lg border-2 border-dashed border-blue-300">
                              <div className="text-center">
                                <div className="flex justify-center mb-2">
                                  <AppIcon name="workdriveSharedDropFile" size={32} alt="Drop files" />
                                </div>
                                <p className="font-medium text-blue-900">Drop files here</p>
                                <p className="text-sm text-blue-700">or click to browse</p>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium">Property_Photos.zip</p>
                                  <p className="text-xs text-gray-500">
                                    Uploaded to 123 Sunset Blvd
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                                <AppIcon name="workdriveSharedClock" size={20} alt="Uploading" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium">Contract_Docs.pdf</p>
                                  <p className="text-xs text-gray-500">Uploading... 75%</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 7 && (
                      // Security & Control
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <h4 className="font-bold text-lg text-center">Security & Control</h4>
                          <div className="space-y-4">
                            <div className="p-4 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3 mb-3">
                                <AppIcon name="workdriveSharedSecurityFeatures" size={20} alt="Security Features" />
                                <span className="font-medium">Security Features</span>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <Check className="w-4 h-4 text-green-600" />
                                  <span>256-bit encryption</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Check className="w-4 h-4 text-green-600" />
                                  <span>Two-factor authentication</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Check className="w-4 h-4 text-green-600" />
                                  <span>Complete audit logs</span>
                                </div>
                              </div>
                            </div>
                            <div className="p-4 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-center gap-3 mb-3">
                                <AppIcon name="workdriveSharedRecentActivity" size={20} alt="Recent Activity" />
                                <span className="font-medium">Recent Activity</span>
                              </div>
                              <div className="space-y-2 text-xs">
                                <div>Sarah J. shared folder with client</div>
                                <div>Mike C. uploaded 12 photos</div>
                                <div>Lisa R. modified contract.pdf</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Mobile App Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side: Mobile Mockup */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:pr-8"
              >
                <div className="relative max-w-[360px] mx-auto">
                  {/* Enhanced Background Effects */}
                  <div className="absolute -inset-12 opacity-20">
                    <div className="absolute top-8 left-8 w-24 h-24 bg-blue-400 rounded-full blur-2xl animate-pulse"></div>
                    <div
                      className="absolute bottom-8 right-8 w-28 h-28 bg-purple-400 rounded-full blur-2xl animate-pulse"
                      style={{ animationDelay: "1s" }}
                    ></div>
                    <div
                      className="absolute top-1/2 left-2 w-20 h-20 rounded-full blur-2xl animate-pulse"
                      style={{ background: "#f58b01", animationDelay: "2s" }}
                    ></div>
                  </div>

                  {/* iPhone 17 Pro Simulator Frame */}
                  <motion.div
                    whileHover={{ y: -10, rotateY: 8, rotateX: 5 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="relative transform-gpu mx-auto w-full max-w-[350px]"
                    style={{ perspective: "1000px" }}
                  >
                    {/* Left side: Action / Silent + Volume Up / Volume Down */}
                    <div
                      className="absolute left-0 top-[88px] -translate-x-[3px] w-[3px] h-[22px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute left-0 top-[140px] -translate-x-[3px] w-[3px] h-[40px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute left-0 top-[190px] -translate-x-[3px] w-[3px] h-[40px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />

                    {/* Right side: Power / Side button */}
                    <div
                      className="absolute right-0 top-[160px] translate-x-[3px] w-[3px] h-[64px] bg-[#2c2c2e] rounded-r-[2px] z-30"
                      aria-hidden="true"
                    />

                    <div className="relative bg-[#0b0b0c] rounded-[3.1rem] p-[11px] shadow-[0_25px_60px_rgba(0,0,0,0.35)] transform-gpu ring-1 ring-black/40">
                      <div className="relative bg-[#1a1a1c] rounded-[2.65rem] p-[2px]">
                        {/* Phone Screen */}
                        <div className="bg-[#f5f5f7] rounded-[2.55rem] overflow-hidden relative min-h-[720px]">
                          {/* Status Bar + Dynamic Island */}
                          <div className="relative px-7 pt-4 pb-2.5 flex justify-between items-center z-10">
                            <span className="text-[13px] font-semibold text-gray-900 tracking-tight">
                              9:41
                            </span>
                            <div className="absolute left-1/2 top-2.5 -translate-x-1/2 w-[102px] h-[30px] bg-black rounded-full z-20 flex items-center justify-end pr-3">
                              <motion.div
                                animate={{ scale: [1, 1.15, 1] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="w-2 h-2 rounded-full bg-[#1a1a1a] ring-1 ring-gray-700"
                              />
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-900">
                              <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor" aria-hidden="true">
                                <rect x="0" y="8" width="2.5" height="4" rx="0.5" />
                                <rect x="4" y="5" width="2.5" height="7" rx="0.5" />
                                <rect x="8" y="2" width="2.5" height="10" rx="0.5" />
                                <rect x="12" y="0" width="2.5" height="12" rx="0.5" />
                              </svg>
                              <svg width="15" height="11" viewBox="0 0 15 11" fill="currentColor" aria-hidden="true">
                                <path d="M7.5 2.2c1.9 0 3.6.7 4.9 1.9l-.9.9A5.3 5.3 0 0 0 7.5 3.5 5.3 5.3 0 0 0 3.5 5l-.9-.9A6.9 6.9 0 0 1 7.5 2.2zm0 2.4c1.2 0 2.3.5 3.1 1.2l-.9.9a2.9 2.9 0 0 0-4.4 0l-.9-.9A4.3 4.3 0 0 1 7.5 4.6zM7.5 9.2a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z" />
                              </svg>
                              <svg width="24" height="12" viewBox="0 0 24 12" fill="none" aria-hidden="true">
                                <rect x="0.5" y="0.5" width="20" height="11" rx="2.5" stroke="currentColor" strokeOpacity="0.35" />
                                <rect x="2" y="2" width="15" height="8" rx="1.5" fill="currentColor" />
                                <path d="M22 4v4a1.5 1.5 0 0 0 0-4z" fill="currentColor" fillOpacity="0.4" />
                              </svg>
                            </div>
                          </div>

                          {/* Mobile App Content */}
                          <div className="px-5 pb-8 pt-3 space-y-4 bg-[#f5f5f7] min-h-[640px]">
                            {/* Header */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5 }}
                              className="flex items-center justify-between mt-14"
                            >
                              <div>
                                <h3 className="font-bold text-[17px] text-gray-900 leading-tight">
                                  My Files
                                </h3>
                                <p className="text-[13px] text-gray-500 mt-1">123 files, 45 folders</p>
                              </div>
                              <div
                                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                                style={{ background: "#236DD9" }}
                              >
                                <CloudUpload className="w-6 h-6 text-white" />
                              </div>
                            </motion.div>

                            {/* Quick Actions */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.7 }}
                              className="grid grid-cols-3 gap-3"
                            >
                              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm text-center">
                                <CameraIcon className="w-6 h-6 text-orange-500 mx-auto mb-1.5" />
                                <div className="text-xs font-semibold text-gray-900">Camera</div>
                              </div>
                              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm text-center">
                                <Upload className="w-6 h-6 text-green-600 mx-auto mb-1.5" />
                                <div className="text-xs font-semibold text-gray-900">Upload</div>
                              </div>
                              <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm text-center">
                                <div className="flex justify-center mb-1.5">
                                  <AppIcon name="workdriveSharedShare" size={24} alt="Share" />
                                </div>
                                <div className="text-xs font-semibold text-gray-900">Share</div>
                              </div>
                            </motion.div>

                            {/* Recent Files */}
                            <div className="space-y-3.5">
                              <div className="flex items-center justify-between px-0.5">
                                <h4 className="font-bold text-[15px] text-gray-900">Recent Files</h4>
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                >
                                  <RefreshCw className="w-4 h-4" style={{ color: "#236DD9" }} />
                                </motion.div>
                              </div>
                              {(
                                [
                                  {
                                    name: "Property_Photos",
                                    type: "folder",
                                    time: "2 hours ago",
                                    color: "bg-blue-100 text-blue-800",
                                    icon: "workdriveSharedFolder",
                                  },
                                  {
                                    name: "Contract.pdf",
                                    type: "file",
                                    time: "Just uploaded",
                                    color: "bg-red-100 text-red-800",
                                    icon: "workdriveSharedPdf",
                                  },
                                  {
                                    name: "Site_Visit.jpg",
                                    type: "file",
                                    time: "1 hour ago",
                                    color: "bg-green-100 text-green-800",
                                    icon: "workdriveSharedJpg",
                                  },
                                ] as {
                                  name: string;
                                  type: string;
                                  time: string;
                                  color: string;
                                  icon: IconName;
                                }[]
                              ).map((file, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.9 + index * 0.1 }}
                                  className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm"
                                >
                                  <div className="flex items-center gap-3.5">
                                    <AppIcon name={file.icon} size={20} alt={file.name} className="flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-bold text-gray-900 truncate">
                                        {file.name}
                                      </p>
                                      <p className="text-xs text-gray-500">{file.time}</p>
                                    </div>
                                    <div className="px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-700 bg-gray-100 whitespace-nowrap flex-shrink-0">
                                      {file.type}
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>

                            {/* Attach to Listing */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 1.3 }}
                              className="bg-white p-4 rounded-2xl border-2 shadow-sm"
                              style={{ borderColor: "#236DD9" }}
                            >
                              <div className="flex items-center gap-3">
                                <AppIcon name="workdriveSharedAttachFile" size={20} alt="Attach to Listing" className="flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-bold text-gray-900">Attach to Listing</p>
                                  <p className="text-xs text-gray-500">123 Sunset Boulevard</p>
                                </div>
                              </div>
                            </motion.div>
                          </div>

                          {/* Home Indicator */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[110px] h-[4px] bg-black/80 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Right side: Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                    Mobile App — Work on the Go
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                    Upload photos/videos from the field, attach to a real estate listing, and share
                    secure links with passwords—all from your phone.
                  </p>
                </div>

                <div className="space-y-6">
                  {(
                    [
                      {
                        title: "Capture and Upload Instantly",
                        description:
                          "Take photos and videos during property visits and upload them directly to the correct listing folder.",
                        icon: "workdriveCaptureUploadInstantly",
                      },
                      {
                        title: "Attach to Listings On-Site",
                        description:
                          "Link documents, photos, and files to specific properties while you're still at the location.",
                        icon: "workdriveAttachToListingOnSite",
                      },
                      {
                        title: "Secure Client Sharing",
                        description:
                          "Generate password-protected links and share them instantly with clients from your mobile device.",
                        icon: "workdriveSecureClientSharing",
                      },
                      {
                        title: "Offline Access",
                        description:
                          "Download important documents for offline viewing when you don't have internet connectivity.",
                        icon: "workdriveOfflineAccess",
                      },
                      {
                        title: "Quick File Management",
                        description:
                          "Move, copy, rename, and organize files with intuitive mobile gestures and actions.",
                        icon: "workdriveQuickFileManagement",
                      },
                      {
                        title: "Real-Time Sync",
                        description:
                          "Changes made on mobile instantly sync across all devices, keeping your team updated.",
                        icon: "workdriveRealTimeSync",
                      },
                    ] as const
                  ).map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center flex-shrink-0">
                        <AppIcon name={feature.icon} size={28} alt={feature.title} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Designed for Real Estate Workflows Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Designed for Real Estate Workflows
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Purpose-built workflows for every aspect of real estate operations, from marketing
                to transactions to brokerage management.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {workflows.map((workflow, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-white p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={workflow.icon} size={36} alt={workflow.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{workflow.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{workflow.description}</p>

                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-sm">Common Files:</h4>
                    {workflow.files.map((file, fileIndex) => (
                      <div
                        key={fileIndex}
                        className="flex items-center gap-2 text-sm text-gray-600"
                      >
                        <File className="w-3 h-3" />
                        <span>{file}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* What's Included Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                What's Included Out of the Box
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Everything you need to get started with professional file management, no setup
                required.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {whatsIncludedSections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-gray-50 p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={section.icon} size={32} alt={section.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h3>
                  <div className="space-y-3">
                    {section.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-3">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* FAQ Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-lg md:text-xl text-gray-700">
                Get answers to common questions about WorkDrive file management system.
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl border border-gray-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <AppIcon name="faqQuestion" size={28} alt="FAQ" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-3">{faq.q}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Enhanced Final CTA */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
          {[...Array(12)].map((_, i) => {
            const startX = Math.random() * 100;
            const startY = Math.random() * 100;
            const endX = Math.random() * 100;
            const endY = Math.random() * 100;

            return (
              <motion.div
                key={i}
                initial={{
                  opacity: 0,
                  left: `${startX}%`,
                  top: `${startY}%`,
                  scale: 0,
                }}
                animate={{
                  opacity: [0, 0.8, 0.6, 1, 0],
                  left: `${endX}%`,
                  top: `${endY}%`,
                  scale: [0, 1, 1.2, 1, 0],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: Math.random() * 8 + 6,
                  repeat: Infinity,
                  delay: i * 0.5,
                  ease: "easeInOut",
                }}
                className="absolute w-8 h-8"
              >
                {i % 4 === 0 ? (
                  <Folder className="w-full h-full text-white" />
                ) : i % 4 === 1 ? (
                  <File className="w-full h-full text-white" />
                ) : i % 4 === 2 ? (
                  <FileImage className="w-full h-full text-white" />
                ) : (
                  <FileText className="w-full h-full text-white" />
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              See how RealEsy WorkDrive transforms file management.
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 leading-relaxed">
              Start your free trial and simplify document workflows instantly.
            </p>

            <div className="flex justify-center">
              <Button
                size="lg"
                className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-12 sm:px-16 py-6 sm:py-7 text-lg sm:text-xl font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.05] transition-all duration-300 border-2 border-white"
              >
                Get Started
                <ArrowRight className="ml-3 w-6 h-6" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
